import {
  useCallback,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { Camera, Paperclip, ScreenShare, Send } from "lucide-react";
import {
  FEEDBACK_CATEGORIES,
  type FeedbackAttachment,
  type FeedbackCategory,
} from "@stockline/shared/types/core/feedback";
import { Button } from "@stockline/ui/components/button";
import { Input } from "@stockline/ui/components/input";
import { Textarea } from "@stockline/ui/components/textarea";
import { CATEGORY_META } from "../lib/categories";
import { capturePage, captureScreenShare } from "../lib/screenshot";
import {
  FileTooLargeError,
  extractFiles,
  formatBytes,
  ingestFile,
} from "../lib/file-attach";
import { AttachmentList } from "./AttachmentList";

export interface FeedbackComposerSubmit {
  title: string;
  description: string;
  category: FeedbackCategory;
  pageUrl: string | null;
  attachments: FeedbackAttachment[];
}

export interface FeedbackComposerProps {
  onSubmit: (input: FeedbackComposerSubmit) => Promise<void>;
  /** Optional secondary action — e.g. "Cancel" in a dialog. */
  onCancel?: () => void;
  /**
   * CSS selector passed to html-to-image so it can skip overlay nodes
   * (instant-feedback dialog uses this to keep itself out of the shot).
   */
  captureExcludeSelector?: string;
  /**
   * Show in compact mode (no outer card padding) — used inside dialogs.
   */
  compact?: boolean;
  /**
   * Slot rendered on the left side of the action footer (counterbalances
   * the Send / Cancel cluster on the right). Used by the dialog to host the
   * "See all feedback" link without colliding with Radix's close X.
   */
  extraFooterLeft?: ReactNode;
}

export function FeedbackComposer({
  onSubmit,
  onCancel,
  captureExcludeSelector,
  compact = false,
  extraFooterLeft,
}: FeedbackComposerProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<FeedbackCategory>("ask");
  const [attachments, setAttachments] = useState<FeedbackAttachment[]>([]);
  const [busy, setBusy] = useState(false);
  const [capturing, setCapturing] = useState<null | "page" | "screen">(null);
  const [dragActive, setDragActive] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragDepth = useRef(0);

  const ingestFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    setAttachError(null);
    const accepted: FeedbackAttachment[] = [];
    const rejected: string[] = [];
    for (const f of files) {
      try {
        accepted.push(await ingestFile(f));
      } catch (err) {
        if (err instanceof FileTooLargeError) {
          rejected.push(`${f.name} (${formatBytes(f.size)})`);
        } else {
          console.warn("[feedback] ingestFile failed", err);
          rejected.push(f.name || "file");
        }
      }
    }
    if (accepted.length > 0) {
      setAttachments((prev) => [...prev, ...accepted]);
    }
    if (rejected.length > 0) {
      setAttachError(rejected.join(", "));
    }
  }, []);

  const onPickFiles = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      void ingestFiles(files);
      e.target.value = "";
    },
    [ingestFiles],
  );

  const onDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer?.types.includes("Files")) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragActive(true);
  }, []);

  const onDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer?.types.includes("Files")) return;
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragActive(false);
  }, []);

  const onDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (e.dataTransfer?.types.includes("Files")) e.preventDefault();
  }, []);

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!e.dataTransfer?.types.includes("Files")) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragActive(false);
      const files = extractFiles(e.dataTransfer.items);
      const candidates =
        files.length > 0 ? files : Array.from(e.dataTransfer.files);
      void ingestFiles(candidates);
    },
    [ingestFiles],
  );

  const onPaste = useCallback(
    (e: ClipboardEvent<HTMLDivElement>) => {
      const files = extractFiles(e.clipboardData?.items ?? null);
      if (files.length === 0) return;
      void ingestFiles(files);
    },
    [ingestFiles],
  );

  const addCapture = useCallback(async () => {
    setCapturing("page");
    try {
      const url = await capturePage({
        excludeSelector: captureExcludeSelector,
      });
      setAttachments((prev) => [
        ...prev,
        {
          dataUrl: url,
          name: "page-capture.jpg",
          mime: "image/jpeg",
          size: estimateDataUrlBytes(url),
        },
      ]);
    } catch (err) {
      console.warn("[feedback] capturePage failed", err);
    } finally {
      setCapturing(null);
    }
  }, [captureExcludeSelector]);

  const addScreenShare = useCallback(async () => {
    setCapturing("screen");
    try {
      const url = await captureScreenShare();
      setAttachments((prev) => [
        ...prev,
        {
          dataUrl: url,
          name: "screen-share.jpg",
          mime: "image/jpeg",
          size: estimateDataUrlBytes(url),
        },
      ]);
    } catch (err) {
      console.warn("[feedback] captureScreenShare failed", err);
    } finally {
      setCapturing(null);
    }
  }, []);

  const submit = useCallback(async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim(),
        category,
        pageUrl: typeof window !== "undefined" ? window.location.href : null,
        attachments,
      });
    } finally {
      setBusy(false);
    }
  }, [title, description, category, attachments, onSubmit]);

  const removeAt = useCallback((idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  return (
    <div
      className={`relative ${compact ? "space-y-4" : "space-y-4 p-6"}`}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onPaste={onPaste}
    >
      {dragActive && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-md border-2 border-dashed border-emerald-400 bg-emerald-50/90 backdrop-blur-[1px]">
          <div className="flex items-center gap-2 text-sm font-medium text-emerald-700">
            <Paperclip size={16} />
            {t("feedback.drop_hint", "Drop files to attach")}
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={onPickFiles}
        data-testid="feedback-file-input"
      />

      <CategoryPicker value={category} onChange={setCategory} />

      <Input
        placeholder={t("feedback.title_placeholder", "What's on your mind?")}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="text-base"
        data-testid="feedback-compose-title"
        autoFocus
      />

      <Textarea
        placeholder={t(
          "feedback.description_placeholder",
          "Add detail — what happened, what you expected, helpful links.",
        )}
        rows={5}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        data-testid="feedback-compose-description"
      />

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addCapture}
            disabled={capturing !== null}
            className="h-7 gap-1.5 border-slate-300 font-medium text-slate-700 hover:border-slate-400 hover:bg-slate-50"
            data-testid="feedback-capture-page"
          >
            <Camera className="h-3.5 w-3.5" />
            {capturing === "page"
              ? t("feedback.capturing", "Capturing…")
              : t("feedback.capture_page", "Capture this page")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addScreenShare}
            disabled={capturing !== null}
            className="h-7 gap-1.5 border-slate-300 font-medium text-slate-700 hover:border-slate-400 hover:bg-slate-50"
            data-testid="feedback-screen-share"
          >
            <ScreenShare className="h-3.5 w-3.5" />
            {capturing === "screen"
              ? t("feedback.capturing", "Capturing…")
              : t("feedback.share_screen", "Share screen")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            className="h-7 gap-1.5 border-slate-300 font-medium text-slate-700 hover:border-slate-400 hover:bg-slate-50"
            data-testid="feedback-upload"
          >
            <Paperclip className="h-3.5 w-3.5" />
            {t("feedback.upload_file", "Attach files")}
          </Button>
          {attachments.length > 0 && (
            <span className="text-xs font-medium text-muted-foreground">
              {t("feedback.attached_count", "{{count}} attached", {
                count: attachments.length,
              })}
            </span>
          )}
        </div>

        {attachError && (
          <p className="text-xs text-amber-700">
            {t("feedback.attach_skipped", "Skipped (too large): {{names}}", {
              names: attachError,
            })}
          </p>
        )}

        <p className="text-[11px] text-muted-foreground">
          {t(
            "feedback.attach_hint",
            "Drop files here, paste from clipboard, or use the buttons above.",
          )}
        </p>

        <AttachmentList attachments={attachments} onRemove={removeAt} />
      </div>

      <div className="flex items-center justify-between gap-3 pt-1">
        <div className="min-w-0">{extraFooterLeft}</div>
        <div className="flex items-center gap-2">
          {onCancel && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              className="h-7 font-medium text-slate-600 hover:text-slate-900"
            >
              {t("feedback.cancel", "Cancel")}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={submit}
            disabled={!title.trim() || busy}
            className="h-7 gap-1.5 border-emerald-300 font-medium text-emerald-700 hover:border-emerald-400 hover:bg-emerald-50 disabled:opacity-50"
            data-testid="feedback-compose-submit"
          >
            <Send className="h-3.5 w-3.5" />
            {busy
              ? t("feedback.sending", "Sending…")
              : t("feedback.submit", "Tell us")}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface CategoryPickerProps {
  value: FeedbackCategory;
  onChange: (c: FeedbackCategory) => void;
}

function CategoryPicker({ value, onChange }: CategoryPickerProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-1.5">
      {FEEDBACK_CATEGORIES.map((c) => {
        const meta = CATEGORY_META[c];
        const Icon = meta.icon;
        const active = c === value;
        return (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition ${
              active
                ? `${meta.className} ring-2 ${meta.ring}`
                : "bg-muted/40 text-muted-foreground hover:bg-muted"
            }`}
            data-testid={`feedback-category-${c}`}
          >
            <Icon size={12} strokeWidth={2.2} />
            {t(`feedback.category.${c}`, c)}
          </button>
        );
      })}
    </div>
  );
}

function estimateDataUrlBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(",");
  const base64 = comma === -1 ? dataUrl : dataUrl.slice(comma + 1);
  return Math.floor((base64.length * 3) / 4);
}
